import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  campaignsApi, CampaignMedia, CampaignStatus, MediaAudience, MediaList, MediaStats, MediaTest,
} from "../../../api/maintenance/campaigns";
import { errMsg, imageProblem, money, num, IMAGE_TYPES, MAX_IMAGE_KB } from "./campaignUtils";
import {
  ArmPlan, CampaignDates, MediaWindow, Segment, WhenPreset, WHO, byPlace, campaignDays, datesOk, dayTime, lastDaysFrom, localZone,
  mediaName, msToLocalInput, parseTs, patchBody, planArm, readPreset, sameSlot, segmentsOf, shortRange, slotKey,
  whenLabel, whoLabel, whoRank, windowFor,
} from "./campaignMedia";

/** All of a campaign's artwork in one card: the main image, which is the
 *  default, and any number of extra images that take over when their window,
 *  audience and A/B arm match. The server picks; nothing here re-implements
 *  that choice -- the timeline is the server's. */

interface Props {
  /** the card's number in the builder */
  step: string;
  campaignId: number | null;
  status: CampaignStatus | null;
  /** UTC; the stored dates once committed, the form's while a draft */
  dates: CampaignDates;
  editable: boolean;
  /** scheduled or live: the rest of the campaign is frozen, this is not */
  lateEdit: boolean;
  /** the main image controls, owned by the builder */
  main: React.ReactNode;
  mainUrl: string | null;
  /** changes when the builder reloads the campaign */
  reloadKey?: string | null;
  /** the campaign's id, saving a new draft first */
  ensureCampaign: () => Promise<number | null>;
  setErr: (m: string) => void;
  setMsg: (m: string) => void;
  onCount?: (n: number) => void;
}

type Draft = {
  id: number | null;            // null = a new image
  file: File | null;
  label: string;
  when: WhenPreset;
  days: string;
  from: string;                 // custom, datetime-local in the viewer's time
  until: string;
  who: MediaAudience | "";      // "" = everyone
  ab: boolean;
};

type Plan =
  | { error: string }
  | { window: MediaWindow; audience: MediaAudience | null; others: CampaignMedia[]; arm: ArmPlan | null };

const blankDraft = (): Draft => ({
  id: null, file: null, label: "", when: "whole", days: "3", from: "", until: "", who: "", ab: false,
});

const inputStyle: React.CSSProperties = { border: "1px solid #e6e8ee", borderRadius: 7, padding: "6px 8px", fontSize: 13 };
const sectionTitle: React.CSSProperties = { fontWeight: 600, fontSize: 13, marginBottom: 8 };

const field = (label: string, node: React.ReactNode, hint?: React.ReactNode) => (
  <label className="ma-select">{label}{node}{hint && <span className="ma-hint">{hint}</span>}</label>
);

const pct = (v: number | null | undefined) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

const Thumb: React.FC<{ url: string | null; size: number }> = ({ url, size }) =>
  url ? (
    <img src={url} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
      style={{ width: size, height: size, objectFit: "cover", borderRadius: 5, border: "1px solid #e6e8ee", flex: "0 0 auto", background: "#f5f6fa" }} />
  ) : (
    <span style={{ width: size, height: size, borderRadius: 5, border: "1px dashed #e6e8ee", flex: "0 0 auto", display: "inline-block" }} />
  );

/** Grey is the main image, indigo another image; the thumbnail and label say which. */
const Strip: React.FC<{ segs: Segment[]; nameOf: (id: number | null) => string; urlOf: (id: number | null) => string | null }> = ({ segs, nameOf, urlOf }) => (
  <div style={{ display: "flex", gap: 2, overflowX: "auto", paddingBottom: 2 }}>
    {segs.map((s, i) => {
      const main = s.ids.every((id) => id == null);
      const blank = main && !urlOf(null);
      const names = s.ids.map(nameOf).join(" / ");
      return (
        <div key={i} title={`${names}\n${dayTime(s.from)} → ${dayTime(s.until)}`}
          style={{ flex: `${Math.max(1, (s.until - s.from) / 3600000)} 1 0px`, minWidth: 140 }}>
          <div style={{ height: 8, borderRadius: 4, background: blank ? "#e6e8ee" : main ? "#8a92a1" : "#5554d3" }} />
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
            {!blank && s.ids.map((id) => <Thumb key={String(id)} url={urlOf(id)} size={22} />)}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{names}</div>
              <div className="ma-hint" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{shortRange(s.from, s.until)}</div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const CampaignArtwork: React.FC<Props> = ({
  step, campaignId, status, dates, editable, lateEdit, main, mainUrl, reloadKey, ensureCampaign, setErr, setMsg, onCount,
}) => {
  const [list, setList] = useState<MediaList | null>(null);
  const [stats, setStats] = useState<MediaStats | null>(null);
  const [loadErr, setLoadErr] = useState("");
  const [statsErr, setStatsErr] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string[]>([]);   // audiences expanded in the timeline

  const showResults = status === "live" || status === "ended";
  const items = (list?.items || []).slice().sort(byPlace);

  const load = useCallback(async (cid: number) => {
    try {
      const r = await campaignsApi.media(cid);
      setList(r.data); setLoadErr("");
    } catch (e: any) { setLoadErr(errMsg(e, "Could not load the other images.")); }
    if (!showResults) return;
    try {
      const r = await campaignsApi.mediaStats(cid);
      setStats(r.data); setStatsErr("");
    } catch (e: any) { setStatsErr(errMsg(e, "Could not load the results.")); }
  }, [showResults]);

  useEffect(() => {
    if (campaignId == null) { setList(null); setStats(null); return; }
    load(campaignId);
  }, [campaignId, reloadKey, load]);

  useEffect(() => { onCount?.(list?.items.length ?? 0); }, [list, onCount]);

  const byId = (id: number | null) => (id == null ? null : items.find((m) => m.id === id) || null);
  const urlOf = (id: number | null) => (id == null ? mainUrl : byId(id)?.image_url ?? null);
  const nameOf = (id: number | null) => {
    if (id == null) return mainUrl ? "Main image" : "No image";
    const m = byId(id);
    return m ? `${mediaName(m)}${m.variant ? ` (${m.variant})` : ""}` : `Image ${id}`;
  };
  const statOf = (id: number | null) => stats?.items.find((s) => s.media_id === id) || null;

  const patchDraft = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));

  const startEdit = (m: CampaignMedia) => {
    const p = readPreset(m, dates);
    setDraft({
      id: m.id, file: null, label: m.label || "", when: p.preset, days: String(p.n ?? 3),
      from: m.show_from ? msToLocalInput(parseTs(m.show_from)) : "",
      until: m.show_until ? msToLocalInput(parseTs(m.show_until)) : "",
      who: m.audience ?? "", ab: m.variant != null,
    });
  };

  // What saving the form would do: the window, the slot it lands in, and the
  // A/B letters. Recomputed every render; the inputs are small.
  const plan: Plan | null = (() => {
    if (!draft) return null;
    const w = windowFor(draft, dates);
    if ("error" in w) return { error: w.error };
    const slot = { audience: draft.who || null, ...w.window };
    const self = draft.id == null ? null : byId(draft.id);
    const others = sameSlot(items, slot, draft.id);
    const stays = !!self && slotKey(self) === slotKey(slot);
    const arm = draft.ab ? planArm(others, stays && self ? self.variant : null) : null;
    return { window: w.window, audience: slot.audience, others, arm };
  })();
  const full = !!plan && !("error" in plan) && !!plan.arm && !plan.arm.variant;

  const save = async () => {
    if (!draft || !plan) return;
    setErr(""); setMsg("");
    if ("error" in plan) { setErr(plan.error); return; }
    const adding = draft.id == null;
    if (adding) {
      if (!draft.file) { setErr("Choose an image."); return; }
      const bad = imageProblem(draft.file);
      if (bad) { setErr(bad); return; }
    }
    if (full) { setErr("That A/B test already has four images."); return; }
    setBusy(true);
    let cid: number | null = null;
    try {
      cid = await ensureCampaign();
      if (cid == null) return;
      // A lone image in the slot becomes A before the new one joins as B.
      for (const r of plan.arm?.relabel || []) await campaignsApi.updateMedia(cid, r.id, { variant: r.variant });
      const fields = { label: draft.label.trim() || null, ...plan.window, audience: plan.audience, variant: plan.arm?.variant ?? null };
      if (adding) await campaignsApi.addMedia(cid, draft.file as File, fields);
      else await campaignsApi.updateMedia(cid, draft.id as number, patchBody(fields));
      setDraft(null);
      setMsg(adding ? "Image added." : "Image saved.");
    } catch (e: any) { setErr(errMsg(e, adding ? "Could not add the image" : "Could not save the image")); }
    finally {
      setBusy(false);
      if (cid != null) await load(cid);
    }
  };

  const remove = async (m: CampaignMedia) => {
    if (campaignId == null || !window.confirm(`Remove “${mediaName(m)}”?`)) return;
    setErr(""); setMsg(""); setBusy(true);
    try {
      await campaignsApi.deleteMedia(campaignId, m.id);
      if (draft?.id === m.id) setDraft(null);
      setMsg("Image removed.");
    } catch (e: any) { setErr(errMsg(e, "Could not remove the image")); }
    finally { setBusy(false); await load(campaignId); }
  };

  const zone = localZone();
  const maxDays = datesOk(dates) ? campaignDays(dates) : undefined;
  const lastHint = (() => {
    if (!draft || draft.when !== "last") return undefined;
    const n = Number(draft.days);
    if (!datesOk(dates) || !Number.isInteger(n) || n < 1) return undefined;
    if (maxDays != null && n > maxDays) return `the campaign runs ${maxDays} day${maxDays === 1 ? "" : "s"}`;
    return `from ${dayTime(parseTs(lastDaysFrom(dates, n)))}`;
  })();

  const abHint = (() => {
    if (!draft || !plan || "error" in plan) return null;
    if (draft.ab && plan.arm) {
      if (!plan.arm.variant) return "Full: A to D are taken.";
      const n = plan.others.length + 1;
      return (<>
        <span className="ma-pill info">Test {plan.arm.variant}</span>{" "}
        {n === 1 ? "Add another with the same When and Who to split." : `A/B test: ${n} images, customers split evenly`}
        {plan.arm.relabel.map((r) => {
          const m = byId(r.id);
          return <span key={r.id}> · “{m ? mediaName(m) : `Image ${r.id}`}” becomes Test {r.variant}</span>;
        })}
      </>);
    }
    // The server refuses this (slot_taken); said here first so it is no surprise.
    if (plan.others.length) {
      const more = plan.others.length - 1;
      return `Same When and Who as “${mediaName(plan.others[0])}”${more ? ` and ${more} more` : ""}. Turn on A/B test to split.`;
    }
    return null;
  })();

  const everyone = list?.timeline.find((t) => t.audience == null) || null;
  const audienceRows = (list?.timeline || []).filter((t) => t.audience != null)
    .sort((a, b) => whoRank(a.audience) - whoRank(b.audience));
  const toggleRow = (k: string) => setOpen((o) => (o.includes(k) ? o.filter((x) => x !== k) : [...o, k]));

  // Main image first, then the list order, then removed images: they were
  // shown, so they keep a row, named and pictured from the stats row itself.
  const resultIds: (number | null)[] = [
    ...(mainUrl || statOf(null) ? [null] : []),
    ...items.map((m) => m.id),
    ...(stats?.items || []).map((s) => s.media_id).filter((id): id is number => id != null && !byId(id)),
  ];
  const resultName = (id: number | null) =>
    id == null ? "Main image" : byId(id) ? nameOf(id) : statOf(id)?.label?.trim() || `Image ${id}`;
  const resultUrl = (id: number | null) =>
    id == null ? mainUrl ?? statOf(null)?.image_url ?? null : byId(id)?.image_url ?? statOf(id)?.image_url ?? null;
  const removedPill = (id: number | null) =>
    id != null && statOf(id)?.removed_at ? <span className="ma-pill muted">Removed</span> : null;
  // Arms in letter order; a removed arm's letter is gone with it, so it goes last.
  const armRank = (id: number) => { const v = byId(id)?.variant; return v ? v.charCodeAt(0) : 1000; };
  const armsOf = (t: MediaTest) => t.arms.slice().sort((a, b) => armRank(a) - armRank(b));

  return (
    <div className="ma-card" style={{ marginBottom: 14 }}>
      <div className="ma-card-h">
        <h3>{step} · Artwork</h3>
        <span className="ma-hint">{!editable ? "frozen" : lateEdit ? `${status} — still editable` : "stays editable once live"}</span>
        {editable && (
          <button className="ma-btn" disabled={busy || !!draft} onClick={() => setDraft(blankDraft())}><Plus size={14} /> Add image</button>
        )}
      </div>

      <div className="ma-card-b">
        <div style={sectionTitle}>Main image</div>
        {main}
      </div>

      <div className="ma-card-b" style={{ borderTop: "1px solid #e6e8ee", paddingBottom: 10 }}>
        <div style={{ ...sectionTitle, marginBottom: 0 }}>
          Other images <span className="ma-hint">· a Who image beats Everyone, then the later start wins</span>
        </div>
        {loadErr && <div className="ma-error" style={{ margin: "8px 0 0" }}>{loadErr}</div>}
      </div>

      {draft && editable && (
        <div className="ma-card-b" style={{ background: "#f7f8ff", borderTop: "1px solid #e6e8ee", borderBottom: "1px solid #e6e8ee", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, alignItems: "end" }}>
          {draft.id == null && field("Image",
            <input type="file" accept={IMAGE_TYPES.join(",")} onChange={(e) => patchDraft({ file: e.target.files?.[0] || null })} style={{ fontSize: 13 }} />,
            `PNG, JPEG or WebP, under ${MAX_IMAGE_KB}KB`)}
          {field("Label", <input value={draft.label} placeholder="Final weekend" onChange={(e) => patchDraft({ label: e.target.value })} style={inputStyle} />)}
          {field("When", (
            <select value={draft.when} onChange={(e) => patchDraft({ when: e.target.value as WhenPreset })}>
              <option value="whole">Whole campaign</option>
              <option value="before">Before it starts</option>
              <option value="last">Last N days</option>
              <option value="custom">Custom</option>
            </select>
          ))}
          {draft.when === "last" && field("Days",
            <input type="number" min={1} max={maxDays} value={draft.days} onChange={(e) => patchDraft({ days: e.target.value })} style={inputStyle} />,
            lastHint)}
          {draft.when === "custom" && (<>
            {field(`From (${zone})`, <input type="datetime-local" value={draft.from} onChange={(e) => patchDraft({ from: e.target.value })} style={inputStyle} />, "blank = no start")}
            {field(`Until (${zone})`, <input type="datetime-local" value={draft.until} onChange={(e) => patchDraft({ until: e.target.value })} style={inputStyle} />, "blank = no end")}
          </>)}
          {field("Who", (
            <select value={draft.who} onChange={(e) => patchDraft({ who: e.target.value as Draft["who"] })}>
              {WHO.map(([k, l]) => <option key={k ?? ""} value={k ?? ""}>{l}</option>)}
            </select>
          ), "higher in the list wins")}
          <label className="ma-select">A/B test
            <span style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "#1f2430", marginTop: 4 }}>
              <input type="checkbox" checked={draft.ab} onChange={(e) => patchDraft({ ab: e.target.checked })} />
              Split with the same When and Who
            </span>
            {abHint && <span className="ma-hint">{abHint}</span>}
          </label>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="ma-btn primary" disabled={busy || full} onClick={save}>{busy ? "Saving…" : draft.id == null ? "Add" : "Save"}</button>
            <button className="ma-btn" disabled={busy} onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="ma-tablewrap"><table className="ma-table">
        <thead><tr><th>Image</th><th>When</th><th>Who</th><th>A/B</th>{editable && <th />}</tr></thead>
        <tbody>
          {items.map((m) => (
            <tr key={m.id} style={draft?.id === m.id ? { background: "#f7f8ff" } : undefined}>
              <td><span style={{ display: "flex", gap: 9, alignItems: "center" }}><Thumb url={m.image_url} size={40} /><b>{mediaName(m)}</b></span></td>
              <td title={m.show_from || m.show_until
                ? `${m.show_from ? dayTime(parseTs(m.show_from)) : "no start"} → ${m.show_until ? dayTime(parseTs(m.show_until)) : "no end"}`
                : undefined}>{whenLabel(m, dates)}</td>
              <td>{whoLabel(m.audience)}</td>
              <td>{m.variant && <span className="ma-pill info">Test {m.variant}</span>}</td>
              {editable && (
                <td><div style={{ display: "flex", gap: 6 }}>
                  <button className="ma-btn" disabled={busy} onClick={() => startEdit(m)}>Edit</button>
                  <button className="ma-btn" disabled={busy} title="Remove" onClick={() => remove(m)}><Trash2 size={13} /></button>
                </div></td>
              )}
            </tr>
          ))}
          {!items.length && (
            <tr><td colSpan={editable ? 5 : 4} className="ma-empty">No other images yet{editable && campaignId == null ? " — adding one saves the draft" : ""}</td></tr>
          )}
        </tbody>
      </table></div>

      {items.length > 0 && (everyone || audienceRows.length > 0) && (
        <div className="ma-card-b">
          <div style={sectionTitle}>Timeline</div>
          {everyone && (<>
            <div className="ma-hint" style={{ marginBottom: 6 }}>Everyone</div>
            <Strip segs={segmentsOf(everyone.spans, items)} nameOf={nameOf} urlOf={urlOf} />
          </>)}
          {audienceRows.map((t) => {
            const k = t.audience as string;
            const isOpen = open.includes(k);
            return (
              <div key={k} style={{ marginTop: 10 }}>
                <button className="ma-btn" onClick={() => toggleRow(k)}>{isOpen ? "▾" : "▸"} {whoLabel(t.audience)}</button>
                {isOpen && <div style={{ marginTop: 8 }}><Strip segs={segmentsOf(t.spans, items)} nameOf={nameOf} urlOf={urlOf} /></div>}
              </div>
            );
          })}
        </div>
      )}

      {showResults && (
        <div className="ma-card-b" style={{ borderTop: "1px solid #e6e8ee" }}>
          <div style={sectionTitle}>Results</div>
          {statsErr && <div className="ma-error">{statsErr}</div>}
          {stats && (<>
            <div className="ma-tablewrap"><table className="ma-table">
              <thead><tr><th>Image</th><th className="num">Shown to</th><th className="num">Topped up</th><th className="num">Conversion</th><th className="num">Cashback paid</th></tr></thead>
              <tbody>
                {resultIds.map((id) => {
                  const s = statOf(id);
                  return (
                    <tr key={String(id)}>
                      <td><span style={{ display: "flex", gap: 8, alignItems: "center" }}><Thumb url={resultUrl(id)} size={28} />{resultName(id)}{removedPill(id)}</span></td>
                      <td className="num" title={s ? `${num(s.via_app)} in the app · ${num(s.via_push)} by push` : undefined}>{num(s?.shown_to)}</td>
                      <td className="num">{num(s?.topped_up)}</td>
                      <td className="num">{pct(s?.conversion)}</td>
                      <td className="num">{money(s?.cashback_amount)}</td>
                    </tr>
                  );
                })}
                {!resultIds.length && <tr><td colSpan={5} className="ma-empty">No images</td></tr>}
              </tbody>
            </table></div>

            {stats.tests.map((t, i) => {
              const arms = armsOf(t);
              return (
                <div key={i} style={{ marginTop: 14 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
                    <b style={{ fontSize: 13 }}>A/B test</b>
                    <span className="ma-hint">{whoLabel(t.audience)} · {whenLabel(t, dates)}</span>
                    {t.small_sample && <span className="ma-pill warn">Too early to call</span>}
                  </div>
                  <div className="ma-tablewrap"><table className="ma-table">
                    <thead><tr><th />{arms.map((id) => <th key={id} className="num">{byId(id)?.variant ? `Test ${byId(id)?.variant}` : "Arm"}</th>)}</tr></thead>
                    <tbody>
                      <tr><td className="sub">Image</td>{arms.map((id) => {
                        const m = byId(id);
                        return (
                          <td key={id} className="num"><span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                            <Thumb url={resultUrl(id)} size={28} />{m ? mediaName(m) : resultName(id)}{removedPill(id)}
                          </span></td>
                        );
                      })}</tr>
                      <tr><td className="sub">Shown to</td>{arms.map((id) => <td key={id} className="num">{num(statOf(id)?.shown_to)}</td>)}</tr>
                      <tr><td className="sub">Topped up</td>{arms.map((id) => <td key={id} className="num">{num(statOf(id)?.topped_up)}</td>)}</tr>
                      <tr><td className="sub">Conversion</td>{arms.map((id) => <td key={id} className="num">{pct(statOf(id)?.conversion)}</td>)}</tr>
                      <tr><td className="sub">Cashback paid</td>{arms.map((id) => <td key={id} className="num">{money(statOf(id)?.cashback_amount)}</td>)}</tr>
                    </tbody>
                  </table></div>
                </div>
              );
            })}
          </>)}
        </div>
      )}
    </div>
  );
};

export default CampaignArtwork;
